import React, { useState, useRef, useEffect } from 'react';
import { Grid, Box, Typography, Button } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { NorthEast } from '@mui/icons-material';
import logo from '../../../assets/Logo.svg';
import noiseSvg from '../../../assets/Noise.svg';
import { navRoutes } from '../../../constants/navRoutes';
import NavigateButton from '../../../components/NavigateButton/NavigateButton';
import { getFooterStyles } from './Footer.styles';
import TermsSection from './TermsSection/TermsSection';

const Footer = () => {
  const navigate = useNavigate();
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isInView) {
            setIsInView(true);
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isInView]);

  const styles = getFooterStyles(noiseSvg, isInView);
  const socialMediaLinks = [
    { name: 'Instagram', url: 'https://www.instagram.com/royalvista.ae' },
    { name: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61584651667710' },
    { name: 'LinkedIn', url: 'https://www.linkedin.com/company/royal-vista-vacation-homes/' },
    { name: 'TikTok', url: 'https://www.tiktok.com/@royalvista.ae?is_from_webapp=1&sender_device=pc' }
  ];

  return (
    <Box ref={containerRef} component="footer" sx={styles.container}>
      <Grid container size={12}>
        {/* Section 1: Logo, Contact Label, Title, and Button */}
        <Grid size={{ xs: 12, md: 6 }} sx={styles.section1}>
          <Box display={{xs: 'none', lg: 'unset'}}>
<img
              src={logo}
              alt="Royal Vista Logo"
              loading="lazy"
              style={styles.logo}
            />
          </Box>
          <Typography sx={styles.contactLabel}>
            Contact
          </Typography>
          <Typography sx={styles.contactTitle}>
            Refined Living,
          </Typography>
          <Typography sx={styles.contactTitle}>
            Thoughtfully Curated
          </Typography>
          <Box sx={{ mt: 5, mb: {xs: 5, lg: 0} }}>
            <NavigateButton
              title="Contact Us"
              isSecondary={true}
              onClick={() => navigate('/contact')}
            />
          </Box>
        </Grid>

        {/* Section 2: Navigation Links and Social Media */}
        <Grid container size={{ xs: 12, md: 6 }} sx={styles.section2}>
          <Grid size={{xs: 0, lg: 2, xl: 4}}/>
          <Grid size={{xs: 6, lg: 5, xl: 4}}>
            {navRoutes.map((route) => (
                  <Button
                    key={route.path}
                    component={RouterLink}
                    to={route.path}
                    sx={styles.navLink}
                  >
                    {route.label}
                  </Button>
                ))} 
                <Button
                  sx={styles.navLink}
                  onClick={() => window.open('https://royalvista.guestyowners.com/login', '_blank')}
                >
                  Owner Login
                </Button>
                <Button
                  sx={styles.navLink}
                  onClick={() => window.open('https://royal-vista.guestybookings.com/en/properties', '_blank')}
                >
                  Book Your Stay
                </Button>
          </Grid>
          <Grid size={{xs: 6, lg: 4}}>
            <Box sx={styles.navLinksColumn}>
              {socialMediaLinks.map((social) => (
                <Button
                  key={social.name}
                  sx={styles.socialButton}
                  onClick={() => window.open(social.url, '_blank')}
                  endIcon={
                    <NorthEast sx={styles.socialIcon} />
                }
                >
                  {social.name}
                </Button>
              ))}
            </Box>
          </Grid>

          <Grid container size={12} mt={{xs: 5, lg: 20}} mb={5}>
            <Grid size={{xs: 0, lg: 2, xl: 4}}/>
            <Grid size={{xs: 6, lg: 5, xl: 4}}>
              <Typography
                component="a"
                href="tel:+97145702703"
                sx={styles.bottomText}
              >
                +971 4 570 270 3
              </Typography>
                <Box sx={styles.bottomTextContainer} my={2}>
                  <Typography sx={styles.bottomText}>
                    Business Hours
                  </Typography>
                  <Typography sx={styles.bottomText}>
                    Mon - Sun  Open 24 Hours
                  </Typography>
                </Box>
                <Typography sx={styles.bottomText}>
                  contact@royal-vista.com
                </Typography>
            </Grid>
            
            <Grid size={{xs: 6, lg: 5, xl: 4}} sx={{alignItems: 'flex-end', display: 'flex', flexDirection: 'column'}}>
              <Typography sx={styles.bottomText}>
                  24/7 customer support
                </Typography>
                <Typography sx={styles.bottomText}>
                  WhatsApp number
                </Typography>
                <Typography
                  component="a"
                  href="tel:+971561012922"
                  sx={{...styles.bottomText,fontWeight: 700}}
                  mt={2}
                >
                  +971 56 101 2922
                </Typography>
            </Grid>
          </Grid>
        
        </Grid>
      </Grid>

      <TermsSection isInView={isInView} />
    </Box>
  );
};

export default Footer;

